import React from 'react';
import { BuildingIcon, CheckCircle2Icon, ClipboardCheckIcon, EyeIcon, EyeOffIcon, FileTextIcon, FolderIcon, UserIcon } from 'lucide-react';
import { QualityReport } from '../../types/quality';
import { getStats } from '../../utils/quality';
import { ProgressRing } from './ProgressRing';
interface ContactField {
  key: string;
  label: string;
  value: string;
  placeholder: string;
  type?: string;
}
interface ContactCardProps {
  label: string;
  icon: typeof UserIcon;
  fields: ContactField[];
  onChange: (key: string, value: string) => void;
}

/** Editable contact block — these values are printed on the sent report. */
function ContactCard({
  label,
  icon: Icon,
  fields,
  onChange
}: ContactCardProps) {
  return <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-292" data-mp="1aoly3y9sxchl">
      <div className="flex items-center gap-2.5" data-id="element-293" data-mp="3qh1p302rkuok">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500" data-id="element-294" data-mp="3qje7fecfp725">
          <Icon className="h-[18px] w-[18px]" data-id="element-295" data-mp="29cfw4ddid443" />
        </span>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-296" data-mp="29cpr6fdeu1gn">
          {label}
        </p>
      </div>

      <div className="mt-4 space-y-3" data-id="element-297" data-mp="2jam1j4yiwsdp">
        {fields.map(field => <label key={field.key} className="block" data-id="element-298" data-mp="2j8iztno0dat5">
            <span className="mb-1 block text-xs font-medium text-stone-500" data-id="element-299" data-mp="2eb9cqeo3db8h">
              {field.label}
            </span>
            <input type={field.type ?? 'text'} value={field.value} onChange={e => onChange(field.key, e.target.value)} placeholder={field.placeholder} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-300" data-mp="2s9z8nqz6d4v9" />
          </label>)}
      </div>
    </div>;
}
interface DetailsTabProps {
  report: QualityReport;
  onToggleVisibility: () => void;
  onDescriptionChange: (description: string) => void;
  onResponsibleChange: (responsible: QualityReport['responsible']) => void;
  onCompanyChange: (company: QualityReport['company']) => void;
}
export function DetailsTab({
  report,
  onToggleVisibility,
  onDescriptionChange,
  onResponsibleChange,
  onCompanyChange
}: DetailsTabProps) {
  const stats = getStats(report);
  const isDone = stats.percent === 100;
  const links = [{
    label: 'Kunde',
    icon: UserIcon,
    title: report.customer.name,
    meta: report.customer.address
  }, {
    label: 'Tilbud',
    icon: FileTextIcon,
    title: report.quote.reference,
    meta: `${report.quote.amount.toLocaleString('da-DK')} kr · ekskl. moms`,
    badge: report.quote.status
  }, {
    label: 'Projekt',
    icon: FolderIcon,
    title: report.project.title,
    meta: report.project.reference
  }];
  return <div className="space-y-5" data-id="element-301" data-mp="2hdnli92gvkk1">
      {/* Progress */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-302" data-mp="29u4lcq13dao8">
        <div className="flex items-center gap-4" data-id="element-303" data-mp="29vfrgwi0euii">
          <ProgressRing percent={stats.percent} size={60} data-id="element-304" data-mp="251uwzefmkio2" />
          <div className="min-w-0" data-id="element-305" data-mp="203t82cvipp4o">
            <h3 className="text-base font-semibold text-stone-900" data-id="element-306" data-mp="204ush7yfbnz2">
              {isDone ? 'Kvalitetssikringen er færdig' : 'Færdiggør kvalitetssikringen'}
            </h3>
            <p className="mt-0.5 text-sm text-stone-500" data-id="element-307" data-mp="2sr5u8lxrnuxg">
              {isDone ? <>
                  Alle {stats.totalPoints} kontrolpunkter er registreret og
                  dokumenteret.
                </> : stats.missingPhoto > 0 ? <>
                  {stats.missingPhoto}{' '}
                  {stats.missingPhoto === 1 ? 'kontrolpunkt mangler' : 'kontrolpunkter mangler'}{' '}
                  foto. Fotografér før det bliver dækket til.
                </> : <>
                  {stats.completedPoints} af {stats.totalPoints} kontrolpunkter
                  er registreret.
                </>}
            </p>
          </div>
        </div>
      </div>

      {/* Visibility */}
      <button onClick={onToggleVisibility} aria-pressed={report.visibleToCustomer} className="flex w-full items-center gap-3 rounded-2xl border border-stone-200 bg-white p-5 text-left transition-colors hover:bg-stone-50" data-id="element-308" data-mp="2h9y7xmcyxfuz">
        <span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${report.visibleToCustomer ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-400'}`} data-id="element-309" data-mp="3eyrt1le54y1p">
          {report.visibleToCustomer ? <EyeIcon className="h-5 w-5" data-id="element-310" data-mp="1omlt5yd1ucc8" /> : <EyeOffIcon className="h-5 w-5" data-id="element-311" data-mp="1tm876a0pbwm3" />}
        </span>
        <span className="min-w-0 flex-1" data-id="element-312" data-mp="1tkdj4mu4vods">
          <span className="block text-base font-semibold text-stone-900" data-id="element-313" data-mp="bobsetw61nnc">
            Synligt for kunden
          </span>
          <span className="mt-0.5 block text-sm text-stone-500" data-id="element-314" data-mp="6oywzkr8b895">
            {report.visibleToCustomer ? 'Kunden kan se rapporten og de godkendte kontrolpunkter.' : 'Rapporten er kun synlig internt.'}
          </span>
        </span>
        <span className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${report.visibleToCustomer ? 'bg-emerald-500' : 'bg-stone-300'}`} data-id="element-315" data-mp="3p3hy404ucia5">
          <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm" style={{
          left: report.visibleToCustomer ? 22 : 2,
          transition: 'left 160ms cubic-bezier(0.23, 1, 0.32, 1)'
        }} data-id="element-316" data-mp="1qfa9i4t9sfq" />
        </span>
      </button>

      {/* KS info */}
      <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-317" data-mp="31eynhdybjtew">
        <div className="flex items-start gap-3" data-id="element-318" data-mp="14dsu55s9o345">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500" data-id="element-319" data-mp="14czwcubacny5">
            <ClipboardCheckIcon className="h-5 w-5" data-id="element-320" data-mp="19ajc5ynmt8s7" />
          </span>
          <div className="min-w-0" data-id="element-321" data-mp="1e4njwk4q1kuc">
            <h3 className="text-base font-semibold text-stone-900" data-id="element-322" data-mp="1e5ouyolqm7c2">KS-info</h3>
            <p className="text-sm text-stone-500" data-id="element-323" data-mp="1j1nloiujg1fn">Hvad rapporten handler om</p>
            <textarea value={report.description} onChange={e => onDescriptionChange(e.target.value)} rows={4} aria-label="Beskrivelse af kvalitetssikringen" placeholder="Beskriv hvad kvalitetssikringen dækker…" className="mt-3 w-full resize-y rounded-xl border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm leading-relaxed text-stone-700 outline-none transition-colors placeholder:text-stone-400 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500" data-id="element-324" data-mp="lfymav2vjxnc" />
          </div>
        </div>
      </div>

      {/* Linked records */}
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white" data-id="element-325" data-mp="3ra29mm4yjx9x">
        <ul className="divide-y divide-stone-100" data-id="element-326" data-mp="3rc569p0m8onm">
          {links.map(link => {
          const Icon = link.icon;
          return <li key={link.label} className="flex items-center gap-3 px-5 py-4" data-id="element-327" data-mp="3hl0t8uui3dvf">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-500" data-id="element-328" data-mp="3cmpj0j0an1co">
                  <Icon className="h-[18px] w-[18px]" data-id="element-329" data-mp="38lxa5qvc2ygx" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-330" data-mp="33kq1lpld9v97">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-331" data-mp="2ylwldpep3q5c">
                    {link.label}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm font-medium text-stone-900" data-id="element-332" data-mp="11r0jf0ol616f">
                    <span className="truncate" data-id="element-333" data-mp="ws76pah4ie7b">{link.title}</span>
                    {link.badge && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700" data-id="element-334" data-mp="anr5vno2l3h9">
                        <CheckCircle2Icon className="h-3 w-3" data-id="element-335" data-mp="3s3h4tme76hkx" />
                        {link.badge}
                      </span>}
                  </p>
                  <p className="mt-0.5 truncate text-sm text-stone-500" data-id="element-336" data-mp="3j314glsr3qka">
                    {link.meta}
                  </p>
                </div>
              </li>;
        })}
        </ul>
      </div>

      {/* Responsible + company — editable, they appear on the sent report */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-337" data-mp="3e47mag2irbds">
        <ContactCard label="Ansvarlig" icon={UserIcon} fields={[{
        key: 'name',
        label: 'Navn',
        value: report.responsible.name,
        placeholder: 'f.eks. Stefan Sørensen'
      }, {
        key: 'email',
        label: 'E-mail',
        value: report.responsible.email,
        placeholder: 'navn@lykke-el.dk',
        type: 'email'
      }, {
        key: 'phone',
        label: 'Telefon',
        value: report.responsible.phone,
        placeholder: '+45 00 00 00 00',
        type: 'tel'
      }]} onChange={(key, value) => onResponsibleChange({
        ...report.responsible,
        [key]: value
      })} data-id="element-338" data-mp="3e6k3us4d7jqb" />

        <ContactCard label="Virksomhed" icon={BuildingIcon} fields={[{
        key: 'name',
        label: 'Navn',
        value: report.company.name,
        placeholder: 'Lykke-EL'
      }, {
        key: 'email',
        label: 'E-mail',
        value: report.company.email,
        placeholder: 'kontakt@lykke-el.dk',
        type: 'email'
      }, {
        key: 'website',
        label: 'Hjemmeside',
        value: report.company.website,
        placeholder: 'www.lykke-el.dk'
      }]} onChange={(key, value) => onCompanyChange({
        ...report.company,
        [key]: value
      })} data-id="element-339" data-mp="3pkcbji2otblk" />
      </div>
    </div>;
}